import React, { useState, useEffect, useCallback } from 'react';

// Helper function to convert VAPID public key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// --- Generate a unique token for this browser/device ---
function getOrCreateUserToken() {
    let token = localStorage.getItem('oht_notification_token');
    if (!token) {
        // Basic pseudo-random token (consider a more robust UUID library if needed)
        token = Date.now().toString(36) + Math.random().toString(36).substring(2);
        localStorage.setItem('oht_notification_token', token);
    }
    return token;
}

// --- IndexedDB Constants (for notification HISTORY) ---
const DB_NAME_HISTORY = 'oht-notifications-db'; // Keep using this for history
const DB_VERSION_HISTORY = 1;
const STORE_NAME_HISTORY = 'notifications';

// --- IndexedDB Helpers (for HISTORY) ---
let panelDbPromiseHistory = null; 
function openPanelDBHistory() {
   if (panelDbPromiseHistory) return panelDbPromiseHistory;
   panelDbPromiseHistory = new Promise((resolve, reject) => {
     const request = indexedDB.open(DB_NAME_HISTORY, DB_VERSION_HISTORY);
     request.onerror = (event) => reject('IndexedDB History error: ' + event.target.errorCode);
     request.onsuccess = (event) => resolve(event.target.result);
     request.onupgradeneeded = (event) => {
         const db = event.target.result;
         if (!db.objectStoreNames.contains(STORE_NAME_HISTORY)) {
            const store = db.createObjectStore(STORE_NAME_HISTORY, { keyPath: 'id', autoIncrement: true });
            store.createIndex('timestamp', 'timestamp', { unique: false });
         }
     };
   });
   return panelDbPromiseHistory;
}

async function getNotificationsFromDBHistory(limit = 20) {
  try {
    const db = await openPanelDBHistory();
    const tx = db.transaction(STORE_NAME_HISTORY, 'readonly');
    const store = tx.objectStore(STORE_NAME_HISTORY);
    const index = store.index('timestamp');
    let notifications = [];
    const request = index.openCursor(null, 'prev'); 
    await new Promise((resolve, reject) => {
        request.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor && notifications.length < limit) {
                notifications.push(cursor.value);
                cursor.continue();
            } else {
                resolve(); 
            }
        };
         request.onerror = (event) => reject(event.target.error);
    });
    return notifications;
  } catch (error) {
    console.error('Error getting notifications history from IndexedDB:', error);
    return [];
  }
}

async function clearNotificationsFromDBHistory() {
    try {
        const db = await openPanelDBHistory();
        const tx = db.transaction(STORE_NAME_HISTORY, 'readwrite');
        const store = tx.objectStore(STORE_NAME_HISTORY);
        await store.clear();
        await tx.complete;
        return true;
    } catch (error) {
        console.error('Error clearing notifications history from IndexedDB:', error);
        return false;
    }
}

// --- Helper function to decode HTML entities --- 
function decodeHtmlEntities(text) {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

function SidebarPanel({ isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('notifications'); // Default to notifications
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true); 
    const [subscriptionError, setSubscriptionError] = useState(null); // Keep for general errors
    // Check if Notification API exists before accessing permission
    const initialPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied'; 
    const [notificationPermission, setNotificationPermission] = useState(initialPermission);
    const [swRegistration, setSwRegistration] = useState(null);
    const [pushSupported, setPushSupported] = useState(true); // Assume supported initially
    const [userToken] = useState(getOrCreateUserToken()); 
    const [notifications, setNotifications] = useState([]);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
    // Simple iOS detection
    const [isIos, setIsIos] = useState(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);

    // Preferences state
    const { availableCategories = {}, isBreakingNewsEnabled = false, groupCategories = false, vapidPublicKey, subscribeNonce, restApiUrl, userId = 0, swPath } = window.ohtFrontendData || {};
    const categoryList = Object.entries(availableCategories).map(([id, name]) => ({ id: parseInt(id, 10), name }));
    const allCategoryIds = categoryList.map(cat => cat.id);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [subscribeBreakingNews, setSubscribeBreakingNews] = useState(false);

    // Close sidebar if clicking outside the panel
    useEffect(() => {
        if (!isOpen) return;

        const handleOutsideClick = (event) => {
            // Check if the click is inside the sidebar panel itself
            if (event.target.closest('.oht-sidebar-panel')) {
                return;
            }
            // Check if the click is on the trigger button (don't close if clicking the button again)
            if (event.target.closest(window.ohtFrontendData?.triggerSelector || '#oht-notification-bell-trigger')){
                 return;
            }
            onClose();
        };

        // Add timeout to prevent immediate closing due to the opening click event bubbling up
        const timerId = setTimeout(() => {
            document.addEventListener('click', handleOutsideClick);
        }, 0);

        return () => {
            clearTimeout(timerId);
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [isOpen, onClose]);

    // Close sidebar on Escape key press
     useEffect(() => {
        if (!isOpen) return;
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);

    // Effect to get SW registration & check push support
    useEffect(() => {
        if ('serviceWorker' in navigator && swPath) {
             navigator.serviceWorker.register(swPath)
                .then(registration => {
                    console.log('Service Worker registered:', registration);
                    setSwRegistration(registration);
                    
                    if (!('pushManager' in registration)) {
                        console.warn('[SidebarPanel] PushManager NOT found in ServiceWorkerRegistration object.');
                        setPushSupported(false);
                        setSubscriptionLoading(false);
                        setActiveTab('settings'); // Default to settings if not supported
                    } else {
                        setPushSupported(true);
                        // Check subscription status
                         registration.pushManager.getSubscription()
                             .then(subscription => {
                                 const subscribed = !!subscription;
                                 setIsSubscribed(subscribed);
                                 // ---> Set default tab based on subscription status <--- 
                                 if (!subscribed && notificationPermission !== 'denied') {
                                     setActiveTab('settings');
                                 }
                             })
                             .catch(err => {
                                 console.error('Error getting subscription:', err);
                                 setSubscriptionError('Could not check subscription status.');
                                 setActiveTab('settings'); // Default to settings on error too
                             })
                             .finally(() => {
                                 setSubscriptionLoading(false); 
                             });
                    }
                })
                .catch(err => {
                    console.error('Service Worker registration failed:', err);
                    setSubscriptionError('Service Worker failed to load.');
                    setPushSupported(false); // Assume not supported if SW fails
                    setSubscriptionLoading(false);
                    setActiveTab('settings');
                });
        } else {
             console.warn('Service workers or swPath not available.');
             setPushSupported(false);
             setSubscriptionLoading(false);
             setActiveTab('settings');
        }

         // Update permission state if it changes externally
         const updatePermission = () => setNotificationPermission(Notification.permission);
         // Use try-catch for browsers that might not support permissions API
         try {
            navigator.permissions?.query({ name: 'notifications' }).then(permissionStatus => {
                permissionStatus.onchange = updatePermission;
            });
         } catch (e) {
             console.warn('Permissions API not fully supported.');
         }
         return () => {
             try {
                 navigator.permissions?.query({ name: 'notifications' }).then(permissionStatus => {
                     if (permissionStatus) permissionStatus.onchange = null;
                 });
             } catch (e) { /* Ignore cleanup error */ }
         }
    }, [swPath]); // Dependency array is correct

    // Effect to fetch preferences *after* subscription status is known and panel is open
    useEffect(() => {
        // Only run if SW is ready, push is supported, panel is open, and we know subscription status
        if (!swRegistration || !pushSupported || !isOpen || subscriptionLoading) return;

        let isMounted = true; 
        // Only fetch from backend if actually subscribed
        if (isSubscribed) {
            console.log('Subscribed. Fetching preferences from backend...');
            // Use the new GET endpoint
            const identifierParam = userId > 0 ? `userId=${userId}` : `userToken=${userToken}`;
            fetch(`${restApiUrl}oht-news-notifications/v1/preferences?${identifierParam}`)
                .then(response => {
                    if (!response.ok) throw new Error('Network response was not ok');
                    return response.json();
                })
                .then(data => {
                    if (!isMounted) return;
                    if (data.success && data.preferences) {
                        // If grouped flag is true, News Alerts toggle should reflect this.
                        // We still receive categories (might be empty array), but grouped flag takes precedence for display.
                        if (groupCategories && data.preferences.grouped) {
                            setSelectedCategories(allCategoryIds); // Check the grouped toggle
                        } else {
                            setSelectedCategories(data.preferences.categories || []);
                        }
                        setSubscribeBreakingNews(data.preferences.breaking || false);
                        // We don't strictly need to store the 'grouped' flag in state 
                        // as we can derive it from groupCategories and selectedCategories.length
                        console.log('Preferences loaded from server:', data.preferences);
                    } else {
                         throw new Error(data.message || 'Failed to fetch preferences');
                    }
                })
                .catch(error => {
                    console.error('Error fetching preferences:', error);
                    if (isMounted) {
                        showStatus('error', 'Could not load your saved preferences.');
                        // Reset local state on error
                        setSelectedCategories([]);
                        setSubscribeBreakingNews(false);
                    }
                });
        } else {
            // Not subscribed, ensure local preferences state is reset
            setSelectedCategories([]);
            setSubscribeBreakingNews(false);
        }
            
         return () => { isMounted = false; };

    }, [swRegistration, isOpen, userId, userToken, isSubscribed, pushSupported, subscriptionLoading, restApiUrl]); // Added dependencies

    // Effect to load notification HISTORY when tab is active
    useEffect(() => {
        if (isOpen && activeTab === 'notifications') {
             setLoadingNotifications(true);
             // Use renamed history function
             getNotificationsFromDBHistory().then(data => {
                 setNotifications(data);
                 setLoadingNotifications(false);
             }).catch(() => setLoadingNotifications(false)); 
        }
    }, [isOpen, activeTab]);

    // --- Helper to set status messages (clears after delay) ---
    const showStatus = (type, text, duration = 4000) => {
        setStatusMessage({ type, text });
        setTimeout(() => setStatusMessage({ type: '', text: '' }), duration);
    };

    // --- Helper Functions for Preference Changes --- 

    // Handles individual category clicks (only when NOT grouped)
    const handleCategoryChange = (categoryId) => {
        setSelectedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    // Handles the single "News Alerts" checkbox toggle (only when grouped)
    const handleGroupedCategoryChange = (isChecked) => {
        setSelectedCategories(isChecked ? allCategoryIds : []);
    };

     const handleBreakingNewsChange = () => {
        setSubscribeBreakingNews(prev => !prev);
    };
    
    // updateSubscriptionOnServer: Sends preferences for an EXISTING subscription
    // OR saves preferences when a NEW subscription is successfully created.
    const updateSubscriptionOnServer = useCallback(async (subscription, preferences = null) => {
         console.log('>>> updateSubscriptionOnServer entered.'); // Log entry
         if (!subscription) { 
             console.log('<<< updateSubscriptionOnServer exiting - no subscription provided.');
             return null; 
         }

         // setSubscriptionLoading(true); // Managed by caller (subscribeUser)
         // setStatusMessage({ type: '', text: '' }); // Managed by caller
         
         const prefsToSend = preferences === null 
                 ? { categories: selectedCategories, breaking: subscribeBreakingNews } 
                 : preferences;
         try {
             console.log('>>> Attempting fetch to /subscribe with prefs:', prefsToSend);
             console.log(`>>> Sending X-WP-Nonce: ${subscribeNonce}`);
             const response = await fetch(`${restApiUrl}oht-news-notifications/v1/subscribe`, {
                 method: 'POST',
                 headers: {
                     'Content-Type': 'application/json',
                     'X-WP-Nonce': subscribeNonce
                 },
                 body: JSON.stringify({
                     token: userToken,
                     endpoint: subscription.endpoint,
                     keys: {
                         p256dh: subscription.toJSON().keys.p256dh,
                         auth: subscription.toJSON().keys.auth
                     },
                     preferences: prefsToSend,
                     userId: userId 
                 }),
             });
             console.log('<<< Fetch response status:', response.status);
             const responseData = await response.json();
             console.log('<<< Fetch response data:', responseData);

             if (!response.ok) {
                 throw new Error(responseData.message || `Server returned status ${response.status}`);
             }
             
             console.log('<<< updateSubscriptionOnServer returning success data.');
             return responseData; 

         } catch (error) {
             console.error('<<< Error in updateSubscriptionOnServer fetch:', error);
             // Let the caller handle UI feedback
             console.log('<<< updateSubscriptionOnServer returning null due to error.');
             return null; // Indicate failure
         } finally {
            // Managed by caller
             // setSubscriptionLoading(false); 
         }
    }, [restApiUrl, subscribeNonce, userToken, userId]); // Dependencies seem ok

    // subscribeUser: Handles the process of enabling notifications
    const subscribeUser = async () => {
        // 1. Check preferences selected
        if (selectedCategories.length === 0 && !subscribeBreakingNews) {
            showStatus('error', 'Please select at least one category or enable breaking news alerts first.');
            return; 
        }
        // 2. Check prerequisites (SW ready, VAPID key exists) - these should ideally be handled by disabling the button
        if (!swRegistration || !vapidPublicKey) {
             showStatus('error', 'Cannot subscribe: Missing configuration or service worker not ready.');
             return;
        }

        setSubscriptionLoading(true);
        setStatusMessage({ type: '', text: '' }); // Clear previous messages

        try {
            // 3. Attempt to subscribe
            const subscription = await swRegistration.pushManager.subscribe({
                 userVisibleOnly: true,
                 applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
             });
             console.log('User IS subscribed.');

            // 4. Send to backend (pass current selections)
            // --- Determine prefs to send based on grouping --- 
            let prefsToSend;
            if (groupCategories) {
                prefsToSend = {
                    grouped: selectedCategories.length > 0, // True if "News Alerts" is checked
                    breaking: subscribeBreakingNews
                };
            } else {
                prefsToSend = {
                    categories: selectedCategories,
                    breaking: subscribeBreakingNews,
                    grouped: false
                };
            }
            // --- End determining prefs ---
            
            console.log('>>> Calling updateSubscriptionOnServer with:', subscription, prefsToSend);
            const result = await updateSubscriptionOnServer(subscription, prefsToSend);
            console.log('<<< updateSubscriptionOnServer returned:', result);

            // 5. Update local state based on backend confirmation
            if (result && result.success) {
                 setIsSubscribed(true);
                 showStatus('success', 'Notifications enabled!');
                 // Optional: Re-sync state from server response if needed
                 // if(result.preferences) { ... }
            } else {
                 // If backend failed, attempt to unsubscribe the browser subscription
                 await subscription?.unsubscribe();
                 setIsSubscribed(false); 
                 showStatus('error', statusMessage.text || 'Failed to save subscription on server.'); 
            }

        } catch (err) {
            console.error('Failed to subscribe the user: ', err);
            if (err.name === 'NotAllowedError') {
                 showStatus('error', 'Notification permission was denied. Please enable it in browser settings.');
            } else {
                 showStatus('error', `Failed to enable notifications: ${err.message}`);
            }
            setIsSubscribed(false); 
            setNotificationPermission(Notification.permission); // Re-check permission after failure
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // unsubscribeUser: Handles disabling notifications
     const unsubscribeUser = async () => {
        if (!swRegistration) return;
        setSubscriptionLoading(true);
        setStatusMessage({ type: '', text: '' });
        try {
            const subscription = await swRegistration.pushManager.getSubscription();
            if (!subscription) {
                setIsSubscribed(false); // Already unsubscribed
                return;
            }
            
            const unsubscribed = await subscription.unsubscribe();
            if (unsubscribed) {
                console.log('User is unsubscribed.');
                 // Send DELETE request to backend
                try {
                    const response = await fetch(`${restApiUrl}oht-news-notifications/v1/subscribe`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-WP-Nonce': subscribeNonce 
                        },
                        body: JSON.stringify({
                             token: userToken,
                             endpoint: subscription.endpoint, 
                         }),
                    });
                     const responseData = await response.json();
                     if (!response.ok) {
                         throw new Error(responseData.message || 'Server error during unsubscription');
                     }
                     setIsSubscribed(false);
                     setSelectedCategories([]); // Reset local prefs
                     setSubscribeBreakingNews(false);
                     showStatus('success', 'Notifications disabled.');
                 } catch (serverError) {
                     console.error('Error removing subscription from server:', serverError);
                     // Even if server fails, keep UI as unsubscribed, but show error
                     setIsSubscribed(false);
                     showStatus('error', `Disabled notifications, but failed to update server: ${serverError.message}`);
                 }
            } else {
                 throw new Error('Unsubscription failed.');
            }
         } catch (err) {
             console.error('Error unsubscribing:', err);
             showStatus('error', `Failed to disable notifications: ${err.message}`);
             // Re-fetch actual subscription state?
             swRegistration?.pushManager?.getSubscription().then(sub => setIsSubscribed(!!sub));
         } finally {
             setSubscriptionLoading(false);
         }
    };

    // handleSavePreferencesClick: ONLY saves prefs if already subscribed
     const handleSavePreferencesClick = useCallback(async () => { // Make async
         if (!isSubscribed || !swRegistration) {
             showStatus('error', 'Cannot save preferences: Not subscribed or SW not ready.');
             return;
         }
         
         setSubscriptionLoading(true); // Set loading TRUE
         setStatusMessage({ type: '', text: '' }); // Clear previous status
         
         try {
             const subscription = await swRegistration.pushManager.getSubscription(); // Await subscription
             
             if (subscription) {
                 // Determine preferences based on grouping
                 let prefsToSend;
                 if (groupCategories) {
                     prefsToSend = {
                         // If grouped category toggle is checked, send grouped=true
                         grouped: selectedCategories.length > 0, 
                         breaking: subscribeBreakingNews
                         // No 'categories' array needed when grouped
                     };
                 } else {
                     prefsToSend = {
                         categories: selectedCategories,
                         breaking: subscribeBreakingNews,
                         grouped: false // Explicitly false when not grouped
                     };
                 }
                 
                 // Await the server update
                 const result = await updateSubscriptionOnServer(subscription, prefsToSend);
                 
                 // Show status based on result
                 if (result && result.success) {
                     showStatus('success', 'Preferences saved successfully!');
                 } else {
                     // Use error message from result if available, otherwise generic
                     const errorMsg = result?.message || 'Failed to save preferences on server.';
                     showStatus('error', errorMsg);
                     // Optional: Re-check subscription status if save failed?
                     // const currentSub = await swRegistration.pushManager.getSubscription();
                     // setIsSubscribed(!!currentSub);
                 }

             } else {
                 showStatus('error', 'Cannot save preferences: Subscription lost.');
                 setIsSubscribed(false); // Correct state if out of sync
             }
         } catch (error) {
             console.error('Error during save preferences click:', error);
             showStatus('error', `Error saving preferences: ${error.message}`);
         } finally {
            setSubscriptionLoading(false); // Set loading FALSE
         }

     }, [
         isSubscribed, 
         swRegistration, 
         updateSubscriptionOnServer, 
         selectedCategories, 
         subscribeBreakingNews, 
         groupCategories, 
         allCategoryIds, 
         showStatus // Include showStatus dependency
        ]);

    // Rename history clear function
    const handleClearNotificationHistory = async () => {
        if (window.confirm('Are you sure you want to clear all notification history?')) {
             const success = await clearNotificationsFromDBHistory();
             if (success) {
                setNotifications([]); 
             }
        }
    };

    // --- Render Logic ---

    return (
        <div className={`oht-sidebar-overlay ${isOpen ? 'is-open' : ''}`}>
            <div className={`oht-sidebar-panel ${isOpen ? 'is-open' : ''}`} role="dialog" aria-modal="true" aria-labelledby="oht-sidebar-title">
                <div className="oht-sidebar-header">
                    <h2 id="oht-sidebar-title" className="screen-reader-text">Notifications Panel</h2>
                    <div className="oht-sidebar-tabs">
                        <button 
                            className={`oht-tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
                            onClick={() => setActiveTab('notifications')}
                            aria-selected={activeTab === 'notifications'}
                            role="tab"
                        >
                            Notifications
                        </button>
                        <button 
                            className={`oht-tab-button ${activeTab === 'settings' ? 'active' : ''}`}
                            onClick={() => setActiveTab('settings')}
                            aria-selected={activeTab === 'settings'}
                            role="tab"
                        >
                            Settings
                        </button>
                    </div>
                    <button onClick={onClose} className="oht-sidebar-close" aria-label="Close Panel">
                        &times; 
                    </button>
                </div>
                <div className="oht-sidebar-content">
                    {activeTab === 'notifications' && (
                         <div role="tabpanel">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h3>Received Notifications</h3>
                                <button 
                                    type="button" 
                                    onClick={handleClearNotificationHistory} 
                                    disabled={notifications.length === 0 || loadingNotifications}
                                    className="button button-small"
                                    style={{ marginLeft: '10px'}}
                                >
                                    Clear History
                                </button>
                            </div>
                            
                            {loadingNotifications && <p>Loading notifications...</p>}
                            
                            {!loadingNotifications && notifications.length === 0 && (
                                <p>No notifications received yet.</p>
                            )}

                            {!loadingNotifications && notifications.length > 0 && (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {notifications.map(note => (
                                        <li key={note.id} style={{ borderBottom: '1px solid #eee', marginBottom: '10px', paddingBottom: '10px' }}>
                                            {note.icon && 
                                                <img src={note.icon} alt="" width="40" height="40" style={{ float: 'left', marginRight: '10px' }} />
                                            }
                                            <div style={{ overflow: 'hidden' }}>
                                                <strong>{note.title}</strong><br/>
                                                {note.body && <span style={{ fontSize: '0.9em', color: '#555' }}>{note.body}</span>}<br/>
                                                {note.url && 
                                                    <a href={note.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9em' }}>View Article</a>
                                                }
                                                <span style={{ fontSize: '0.8em', color: '#888', display: 'block', marginTop: '3px' }}>
                                                    {new Date(note.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    {activeTab === 'settings' && (
                        <div role="tabpanel">
                            <h3>Manage Subscriptions</h3>

                            {/* --- Display Area for Status/Errors --- */} 
                            {statusMessage.text && (
                                <div 
                                   className={`oht-status-message oht-status-${statusMessage.type}`}
                                   role="alert"
                                >
                                    {statusMessage.text}
                                </div>
                            )}

                            {/* --- Display if Push Not Supported --- */} 
                            {!pushSupported && (
                                 <div className="oht-status-message oht-status-error">
                                     {isIos ? (
                                         <span>
                                             Push Notifications are currently only supported on iOS/iPadOS when the website is added to your Home Screen. 
                                             Please use the Share button in Safari, select "Add to Home Screen", and open the site from the new Home Screen icon.
                                         </span>
                                     ) : (
                                         'Push Notifications are not supported by this browser.'
                                     )}
                                 </div>
                            )}

                            {/* --- Display if Permission Denied --- */} 
                            {pushSupported && notificationPermission === 'denied' && (
                                 <p className="oht-status-message oht-status-error">
                                     Notification permission is blocked. Please enable it in your browser settings.
                                 </p>
                            )}

                            {/* --- Preference Selection (Conditional based on push support) --- */} 
                            {pushSupported && (
                                <>
                                    <p>Select the topics you want to receive notifications for:</p>
                                     
                                     {/* Single Group for Topics */} 
                                     <div className="oht-setting-group">
                                         <h4>Topics</h4> {/* Single Heading */} 
                                         
                                         {/* --- CATEGORY SELECTION (Conditional Grouping) --- */} 
                                         {groupCategories ? (
                                             // --- Render Grouped "News Alerts" --- 
                                             <div className="oht-topic-item"> 
                                                 <label>
                                                     <input 
                                                         type="checkbox" 
                                                         name="news_alerts" 
                                                         checked={selectedCategories.length > 0}
                                                         onChange={(e) => handleGroupedCategoryChange(e.target.checked)}
                                                         disabled={subscriptionLoading} 
                                                     /> News Alerts
                                                 </label>
                                             </div>
                                         ) : (
                                             // --- Render Individual Categories --- 
                                             categoryList.length > 0 && (
                                                 // No extra heading needed here
                                                 <ul>
                                                     {categoryList.map(category => (
                                                         <li key={category.id} className="oht-topic-item"> 
                                                             <label>
                                                                 <input 
                                                                     type="checkbox" 
                                                                     name={`category_${category.id}`} 
                                                                     checked={selectedCategories.includes(category.id)}
                                                                     onChange={() => handleCategoryChange(category.id)}
                                                                     disabled={subscriptionLoading} 
                                                                 /> {decodeHtmlEntities(category.name)}
                                                             </label>
                                                         </li>
                                                     ))}
                                                 </ul>
                                             )
                                         )}
                                         {/* --- END CATEGORY SELECTION --- */} 
            
                                        {/* --- BREAKING NEWS SELECTION (now under Topics group) --- */} 
                                        {isBreakingNewsEnabled && (
                                             <div className="oht-topic-item"> 
                                                 <label>
                                                     <input 
                                                         type="checkbox" 
                                                         name="breaking_news" 
                                                         checked={subscribeBreakingNews}
                                                         onChange={handleBreakingNewsChange}
                                                         disabled={subscriptionLoading}
                                                     /> Breaking News
                                                 </label>
                                             </div>
                                        )}
                                        {/* --- END BREAKING NEWS --- */} 
                                     </div> {/* End Single Group for Topics */} 
                                </>)} 

                             {/* --- Action Buttons (Conditional) --- */} 
                            {pushSupported && notificationPermission !== 'denied' && (
                                <div className="oht-action-buttons" style={{ marginTop: '15px' }}> {/* <-- Keep Wrapper Div */} 
                                     {!isSubscribed && (
                                         <button 
                                             type="button" 
                                             onClick={subscribeUser}
                                             disabled={subscriptionLoading}
                                             aria-live="polite"
                                             // Specific class for CSS targeting
                                             className="oht-sidebar-button oht-button-enable"
                                         >
                                             {subscriptionLoading ? 'Enabling...' : 'Enable Notifications'}
                                         </button>
                                     )}
                                     {isSubscribed && (
                                         <>
                                             <button 
                                                 type="button" 
                                                 onClick={handleSavePreferencesClick}
                                                 disabled={subscriptionLoading}
                                                 aria-live="polite"
                                                 // Restore generic button class or keep specific for easier targeting?
                                                 // Let's use a generic one plus specific for state
                                                 className="oht-sidebar-button oht-button-save"
                                             >
                                                 {subscriptionLoading ? 'Saving...' : 'Save Preferences'}
                                             </button>
                                             <button 
                                                 type="button" 
                                                 onClick={unsubscribeUser}
                                                 disabled={subscriptionLoading}
                                                 aria-live="polite"
                                                 // Restore button-small for specific styling
                                                 className="oht-sidebar-button oht-button-disable button-small"
                                             >
                                                  {subscriptionLoading ? 'Disabling...' : 'Disable Notifications'}
                                             </button>
                                         </>
                                     )}
                                 </div>
                             )}
                            
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SidebarPanel; 